import {observer} from 'mobx-react-lite'
import React, {useEffect, useRef, useState} from 'react'
import ScrollBar from 'react-perfect-scrollbar'
import {Lang} from 'yougile-lang'
import {Time, Util} from 'yougile-util'
import {
  SprintStickerState,
  StickerColor,
  StickerIcon,
  StickerStateId,
  StringStickerState
} from 'yougile-model'
import {PermissionsChecker} from '@client/permissions-checker'
import {Tracking, TrackingEvent} from '@common/tracking'
import {LinkButtonNew} from '@components/common/base/buttons'
import {CheckboxBase} from '@components/common/base/checkbox-new/checkbox-base'
import {Sticker} from '@components/common/base/sticker/sticker'
import {WithPerfectScrollbar} from '@components/common/with-scrollbar/with-scrollbar'
import {DraggableNative} from '@components/draggable-native'
import {MessageBox} from '@components/message-box'
import {StickerStringStateEditor} from '@components/sticker/my-stickers/sticker-editor/string-state-edit/sticker-string-state-editor'
import {TwStickerMenu} from '@components/sticker/sticker-menu/containers'
import {StickerMenuHeader} from '@components/sticker/sticker-menu/sticker-menu-header'
import {
  TwStickerEditState,
  TwStickerItemState,
  TwStickerItemStateCheckbox,
  TwStickerItemStateSticker,
  TwStickerMenuBody
} from '@components/sticker/sticker-menu/sticker-menu-string/containers'
import {SprintStateEdit} from '@components/sticker/sticker-menu/sticker-menu-string/sprint-state-edit'
import {
  getStateColor,
  getStatesList,
  StickerState
} from '@components/sticker/sticker-menu/sticker-menu-string/utils'
import {useStickerStore} from '@components/sticker/sticker-store'
import {useForceUpdate} from '@containers/hooks'
import {globals} from '@extensions/globals-base'
import {PageService} from '@extensions/page-service'
import {BoardFilters} from '@extensions/sticker/board-filters'
import {SprintCtl} from '@extensions/sticker/controllers/sprint-ctl'
import {stickerCtl} from '@extensions/sticker/sticker'
import {Sticker as StickerExtension} from '@extensions/sticker/sticker'

const defaultState: StickerState = {
  id: Util.makeShortId() as StickerStateId,
  name: ''
}

// eslint-disable-next-line max-lines-per-function
export const StickerMenuStringPanel = observer(() => {
  const [state, store] = useStickerStore()
  const forceUpdate = useForceUpdate()
  const [editingState, setEditingState] = useState<StickerState>()
  const [isAdding, setIsAdding] = useState(false)
  const isDark = globals.theme === 'dark'
  const scrollContainer = useRef<ScrollBar>(null)

  const allStates = getStatesList(store.sticker)
  const isSprintSticker = store.stickerData.stickerType === 'sprint'
  const board = PageService.getCurrentBoard()
  const isEditAvailable = PermissionsChecker.checkEditBoardStickers(board)

  useEffect(() => {
    if (allStates?.length && scrollContainer.current) {
      scrollContainer.current.updateScroll()
    }
  }, [allStates?.length])

  const addState = () => {
    if (!PermissionsChecker.checkEditBoardStickers(board)) {
      MessageBox.warn(Lang.msg.permission.noEditObject)
    } else {
      setEditingState({
        id: Util.makeShortId() as StickerStateId,
        name: '',
        color: 'sticker-gray'
      })
      setIsAdding(true)
      void Tracking.send(TrackingEvent.boardSettingsEditCustomStickerViaBoard)
    }
  }
  const cancelEditing = () => {
    setIsAdding(false)
    setEditingState(undefined)
  }
  const saveSticker = () => {
    if (!editingState) {
      cancelEditing()
      return
    }
    if (isSprintSticker) {
      if (isAdding) {
        stickerCtl.Sprint.addState(
          store.sticker,
          editingState as SprintStickerState
        )
      } else {
        stickerCtl.Sprint.changeState(
          store.sticker,
          editingState.id,
          editingState as SprintStickerState
        )
      }
    } else {
      if (isAdding) {
        stickerCtl.String.addState(
          store.sticker,
          editingState as StringStickerState
        )
      } else {
        stickerCtl.String.changeState(
          store.sticker,
          editingState.id,
          editingState as StringStickerState
        )
      }
    }
    if (state.filterValues?.includes(editingState.id)) {
      BoardFilters.save()
    }

    cancelEditing()
    forceUpdate()
  }

  const toggleFilter = (stateId: StickerStateId) => {
    store.toggleFilter([stateId])
  }
  const toggleAll = () => {
    if (state.filterValues?.length) {
      store.setFilters([])
    } else {
      store.setFilters(allStates.map(state => state.id))
    }
  }

  const onChangeEditingName = (name: string, stickerState?: StickerState) => {
    setEditingState(prev => ({
      ...(prev ?? (stickerState || defaultState)),
      name
    }))
  }

  const onChangeSprintDates = (
    [begin, end]: [Date, Date],
    stickerState?: StickerState
  ) => {
    setEditingState(prev => ({
      ...(prev ?? (stickerState || defaultState)),
      begin: Time.toTimestamp(begin),
      end: Time.toTimestamp(end)
    }))
  }
  const getFiltersCheckboxValue = (): boolean | undefined => {
    if (state.filterValues?.length) {
      return state.filterValues.length !== allStates.length ? undefined : true
    }

    return false
  }

  return (
    <TwStickerMenu className="min-w-[300px] py-8">
      <StickerMenuHeader
        stickerId={store.sticker.id}
        title={store.sticker.title}
        className="px-12 pt-4 pb-8"
      >
        <div className="ml-auto text-12 flex gap-8 items-center">
          <div className="text-secondary">
            {Lang.msg.filter}
            {state.filterValues?.length ? `(${state.filterValues.length})` : ''}
          </div>
          <CheckboxBase value={getFiltersCheckboxValue()} onClick={toggleAll} />
        </div>
      </StickerMenuHeader>
      <WithPerfectScrollbar
        ref={scrollContainer}
        className={`max-h-250 ${
          isDark ? 'perfect-scrollbar-with-custom-scroll' : ''
        }`}
        wheelPropagation
        wheelSpeed={1.5}
      >
        <TwStickerMenuBody>
          {allStates.map(stickerState => {
            if (editingState?.id === stickerState.id) {
              return (
                <TwStickerEditState
                  key={editingState.id}
                  className="relative z-10 py-4 px-6"
                >
                  {isSprintSticker ? (
                    <SprintStateEdit
                      key={editingState.id}
                      state={editingState}
                      onSave={saveSticker}
                      onChangeDates={range =>
                        onChangeSprintDates(range, stickerState)
                      }
                      onChange={(name: string) => {
                        onChangeEditingName(name, stickerState)
                      }}
                    />
                  ) : (
                    <StickerStringStateEditor
                      key={editingState.id}
                      icon={store.stickerData.icon as StickerIcon}
                      value={editingState.name}
                      selectedColor={editingState?.color}
                      inputMaxWidth="max-w-[150px]"
                      onChange={(name: string) =>
                        onChangeEditingName(name, stickerState)
                      }
                      cancel={cancelEditing}
                      onEnterSave={saveSticker}
                      onSave={saveSticker}
                      onClickOnColor={(color: StickerColor) => {
                        setEditingState(prev => ({
                          ...(prev ?? stickerState),
                          color
                        }))
                      }}
                    />
                  )}
                </TwStickerEditState>
              )
            }

            const stateColor = getStateColor(
              stickerState,
              store.stickerData.stickerType
            )

            return (
              <TwStickerItemState
                key={stickerState.id}
                className="sticker-group"
                onClick={() => toggleFilter(stickerState.id)}
              >
                <TwStickerItemStateSticker>
                  <DraggableNative
                    onDragStarted={store.startDragging}
                    onDragStartedCallback={store.closeStickerMenu}
                    onDragEnded={store.endDragging}
                    transfer={{
                      type: 'sticker',
                      stickerId: store.sticker.id,
                      value: stickerState.id
                    }}
                  >
                    <Sticker
                      isDraggable
                      icon={isSprintSticker ? '' : store.stickerData.icon}
                      title={
                        <>
                          {stickerState.name}{' '}
                          {isSprintSticker && (
                            <span className="font-semibold">
                              {SprintCtl.formatInterval(stickerState)}
                            </span>
                          )}
                        </>
                      }
                      color={stateColor}
                      iconColor={
                        isSprintSticker ? undefined : `icon-${stateColor}`
                      }
                      iconBackgroundColor={
                        isSprintSticker ? undefined : `icon-${stateColor}`
                      }
                    />
                  </DraggableNative>
                </TwStickerItemStateSticker>
                <TwStickerItemStateCheckbox>
                  <CheckboxBase
                    value={state.filterValues?.includes(stickerState.id)}
                    onClick={event => {
                      event.stopPropagation()
                      toggleFilter(stickerState.id)
                    }}
                  />
                </TwStickerItemStateCheckbox>
              </TwStickerItemState>
            )
          })}
          {!isAdding &&
            isEditAvailable &&
            !StickerExtension.isReadonly(store.sticker) && (
              <div className="px-12 pt-4">
                <LinkButtonNew
                  icon="IconCustomPlus"
                  onClick={addState}
                  title={Lang.msg.add}
                />
              </div>
            )}
          {isAdding &&
            editingState &&
            (isSprintSticker ? (
              <SprintStateEdit
                key={editingState.id}
                state={editingState}
                onSave={saveSticker}
                onChangeDates={onChangeSprintDates}
                onChange={onChangeEditingName}
              />
            ) : (
              <div className="px-12 py-4">
                <StickerStringStateEditor
                  key={editingState.id}
                  icon={store.stickerData.icon as StickerIcon}
                  value={editingState.name}
                  selectedColor={editingState.color}
                  inputMaxWidth="max-w-[150px]"
                  colorsClassName="relative !top-0"
                  onChange={onChangeEditingName}
                  cancel={cancelEditing}
                  onEnterSave={saveSticker}
                  onSave={saveSticker}
                  onClickOnColor={(color: StickerColor) => {
                    setEditingState(prev => {
                      if (prev) {
                        return {
                          ...prev,
                          color
                        }
                      }
                      return prev
                    })
                  }}
                />
              </div>
            ))}
        </TwStickerMenuBody>
      </WithPerfectScrollbar>
    </TwStickerMenu>
  )
})
